import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { writeAuditLog } from '../lib/auditLog'
import type { Team, TeamInsert, TeamUpdate, TeamMember, TeamMemberInsert, TeamMemberUpdate } from '../types/database'

export function useMyTeams(userId: string | null | undefined) {
  return useQuery({
    queryKey: ['myTeams', userId],
    queryFn: async () => {
      if (!userId) return []

      const { data, error } = await supabase
        .from('team_members')
        .select('team_id, role, teams(*)')
        .eq('user_id', userId)
        .order('joined_at', { ascending: false })

      if (error) throw error
      const rows = data as unknown as Array<{ role: TeamMember['role']; teams: Team | Team[] | null }>
      return rows
        .map((item) => {
          const team = Array.isArray(item.teams) ? item.teams[0] : item.teams
          if (!team) return null
          return { ...team, userRole: item.role }
        })
        .filter((item): item is Team & { userRole: TeamMember['role'] } => Boolean(item))
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useTeam(teamId: string | null | undefined) {
  return useQuery({
    queryKey: ['team', teamId],
    queryFn: async () => {
      if (!teamId) return null

      const { data, error } = await supabase
        .from('teams')
        .select('*')
        .eq('id', teamId)
        .single()

      if (error) throw error
      return data as Team
    },
    enabled: !!teamId,
  })
}

export function useCreateTeam() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (team: Omit<TeamInsert, 'created_by'>) => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('teams')
        .insert({ ...team, created_by: user.id } as Record<string, unknown>)
        .select()
        .single()

      if (error) throw error
      return data as Team
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myTeams'] })
    },
  })
}

export function useUpdateTeam() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: TeamUpdate }) => {
      const { data, error } = await supabase
        .from('teams')
        .update({ ...updates, updated_at: new Date().toISOString() } as Record<string, unknown>)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as Team
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['myTeams'] })
      queryClient.invalidateQueries({ queryKey: ['team', id] })
    },
  })
}

export function useDeleteTeam() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('teams')
        .delete()
        .eq('id', id)
        .eq('created_by', user.id)
        .select()
        .single()

      if (error) throw error

      await writeAuditLog({
        action: 'team.delete',
        resourceType: 'team',
        resourceId: id,
        oldValue: data as Record<string, unknown>,
        newValue: null,
      })

      return data as Team
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myTeams'] })
    },
  })
}

export function useTeamMembers(teamId: string | null | undefined) {
  return useQuery({
    queryKey: ['teamMembers', teamId],
    queryFn: async () => {
      if (!teamId) return []

      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .eq('team_id', teamId)
        .order('joined_at', { ascending: true })

      if (error) throw error
      const members = data as TeamMember[]

      if (members.length === 0) return []

      const userIds = members.map(m => m.user_id)
      const emailMap = await getUsersEmailsForTeam(userIds, teamId)

      return members.map(m => ({
        ...m,
        email: emailMap[m.user_id] || null
      })) as (TeamMember & { email: string | null })[]
    },
    enabled: !!teamId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useAddTeamMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (member: Omit<TeamMemberInsert, 'invited_by'>) => {
      const { data: { user } } = await supabase.auth.getUser()

      const { data, error } = await supabase
        .from('team_members')
        .insert({ ...member, invited_by: user?.id } as Record<string, unknown>)
        .select()
        .single()

      if (error) throw error
      return data as TeamMember
    },
    onSuccess: (_, { team_id }) => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers', team_id] })
    },
  })
}

export function useUpdateTeamMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, teamId, updates }: { id: string; teamId: string; updates: TeamMemberUpdate }) => {
      const { data, error } = await supabase
        .from('team_members')
        .update(updates as Record<string, unknown>)
        .eq('id', id)
        .eq('team_id', teamId)
        .select()
        .single()

      if (error) throw error
      return data as TeamMember
    },
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers', teamId] })
    },
  })
}

export function useRemoveTeamMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, teamId }: { id: string; teamId: string }) => {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', id)
        .eq('team_id', teamId)

      if (error) throw error
    },
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers', teamId] })
    },
  })
}

export async function findUserByEmailForTeam(email: string, teamId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('find_user_by_email_for_team', {
    p_email: email,
    p_team_id: teamId
  })
  if (error) throw error
  return data as string | null
}

export async function getUsersEmailsForTeam(userIds: string[], teamId: string): Promise<Record<string, string>> {
  if (userIds.length === 0) return {}
  const { data, error } = await supabase.rpc('get_users_emails_for_team', {
    p_user_ids: userIds,
    p_team_id: teamId
  })
  if (error) throw error
  const emailMap: Record<string, string> = {}
  if (data) {
    for (const row of data as { user_id: string; email: string }[]) {
      emailMap[row.user_id] = row.email
    }
  }
  return emailMap
}
